// ---------------------------------------------------------------------------
// Telling the browser what paper it is printing on.
//
// A thermal printer does not negotiate. Send an 80 mm layout to a 58 mm roll
// and the right-hand column of every line is simply gone - and because the
// total is on the right, what is gone is the amount the customer owes.
//
// `@page { size }` cannot read a CSS custom property in any browser that
// matters, so the rule is written into a <style> element instead. The content
// width IS a custom property, because ordinary rules can read those.
//
// The two figures per roll are the paper width and the printable width. They
// differ because thermal print heads do not reach the edge; 80 mm rolls print
// about 72 mm and 58 mm rolls about 50 mm. Using the paper width as the
// content width is the single most common way receipt printing goes wrong.
// ---------------------------------------------------------------------------

export type ReceiptWidth = 58 | 80;

const PAPER = {
  58: { paperMm: 58, contentMm: 50 },
  80: { paperMm: 80, contentMm: 72 },
} as const;

const STYLE_ID = 'yaghi-print-page-size';

/// Read the shop's setting, falling back to 80 mm.
///
/// The setting arrives as a string from the settings table and could be
/// anything; only the two widths this system lays out for are accepted.
export function receiptWidthFrom(setting: string | undefined): ReceiptWidth {
  return setting?.trim() === '58' ? 58 : 80;
}

/// Point the print stylesheet at a given roll.
///
/// Called just before `window.print()`. Safe to call repeatedly - the style
/// element is reused rather than piling up.
export function applyReceiptPaper(width: ReceiptWidth): void {
  if (typeof document === 'undefined') return;

  const spec = PAPER[width];

  document.documentElement.style.setProperty('--receipt-content-width', `${spec.contentMm}mm`);

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  style.textContent = `@media print { @page { size: ${spec.paperMm}mm auto; margin: 3mm; } }`;
}

/// The paper a TEST print goes on.
///
/// Two answers, because a shop tests on two things:
///
///   THERMAL - the roll the receipt is actually for. No page margin: the
///             content width already excludes the print head's unprintable
///             edge (72 mm of an 80 mm roll), so adding a margin on top would
///             narrow the paper twice.
///
///   A4      - an ordinary office printer, which is what most shops have on
///             the desk while they are still deciding on a layout. The receipt
///             is placed on the sheet AT ITS REAL WIDTH and is never stretched
///             to fill it: an 80 mm design must measure 80 mm on the A4 page,
///             or the test tells you nothing about whether it fits the roll.
export type TestPaper = { kind: 'THERMAL'; width: ReceiptWidth } | { kind: 'A4' };

export function applyTestPaper(paper: TestPaper): void {
  if (typeof document === 'undefined') return;

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  if (paper.kind === 'A4') {
    // The margin is the OFFICE printer's, not the receipt's. The receipt keeps
    // its own millimetre width inside it.
    style.textContent = '@media print { @page { size: A4 portrait; margin: 12mm; } }';
    return;
  }

  const spec = PAPER[paper.width];
  document.documentElement.style.setProperty('--receipt-content-width', `${spec.contentMm}mm`);
  style.textContent = `@media print { @page { size: ${spec.paperMm}mm auto; margin: 0; } }`;
}

/// Point it at label stock instead.
///
/// Labels print on a sheet or a roll of their own, so the page is sized to the
/// label rather than to a receipt roll.
export function applyLabelPaper(widthMm: number, heightMm: number): void {
  if (typeof document === 'undefined') return;

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  style.textContent = `@media print { @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; } }`;
}

/// The printable content width for a roll, in millimetres.
///
/// Exported for the preview, so what is on screen is the same width as what
/// comes out of the printer.
export function contentWidthMm(width: ReceiptWidth): number {
  return PAPER[width].contentMm;
}

/// The ROLL's width, in millimetres.
///
/// Different from `contentWidthMm` and the difference matters: an 80 mm roll
/// prints 72 mm of content, because the print head does not reach the edge.
/// The content figure is what the layout uses; this one is what somebody holds
/// a ruler against.
export function paperWidthMm(width: ReceiptWidth): number {
  return PAPER[width].paperMm;
}
